import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowLeft, Bookmark, BookmarkCheck, Grid2X2, HeartPulse, Leaf, Loader2, Sparkles, Utensils, Wind, X } from "lucide-react";
import { Link } from "react-router-dom";
import TipCard, { TipRecord } from "@/components/TipCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const ExploreTipsPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const bn = language === "bn";
  const [tips, setTips] = useState<TipRecord[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [savedTipIds, setSavedTipIds] = useState<Set<string>>(new Set());
  const [savingTipId, setSavingTipId] = useState<string | null>(null);
  const [selectedTip, setSelectedTip] = useState<TipRecord | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const categories = [
    { value: "All", label: bn ? "সব" : "All", icon: Sparkles },
    { value: "Nutrition", label: bn ? "পুষ্টি" : "Nutrition", icon: AppleIcon },
    { value: "Lifestyle", label: bn ? "জীবনযাপন" : "Lifestyle", icon: Leaf },
    { value: "Mental wellness", label: bn ? "মানসিক সুস্থতা" : "Mental wellness", icon: HeartPulse },
    { value: "Fitness", label: bn ? "ফিটনেস" : "Fitness", icon: Wind },
    { value: "General", label: bn ? "সাধারণ" : "General", icon: Grid2X2 },
  ];

  useEffect(() => {
    let active = true;
    const loadTips = async () => {
      setLoading(true);
      setError("");
      const { data, error: tipsError } = await (supabase.from("health_tips" as any) as any)
        .select("id, author_id, title, body, category, image_url, created_at")
        .eq("status", "published")
        .order("created_at", { ascending: false });

      if (!active) return;
      if (tipsError) {
        setError(tipsError.message);
        setLoading(false);
        return;
      }

      const rows = (data ?? []) as Array<TipRecord & { author_id: string }>;
      const authorIds = [...new Set(rows.map((tip) => tip.author_id).filter(Boolean))];
      let profiles: Array<{ user_id: string; full_name: string | null; profile_photo: string | null; role: string | null }> = [];
      if (authorIds.length > 0) {
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("user_id, full_name, profile_photo, role")
          .in("user_id", authorIds);
        if (profileError) {
          setError(profileError.message);
          setLoading(false);
          return;
        }
        profiles = profileData ?? [];
      }

      const profileMap = new Map(profiles.map((profile) => [profile.user_id, profile]));
      setTips(rows.map(({ author_id, ...tip }) => ({ ...tip, author: profileMap.get(author_id) ?? null })));

      if (user) {
        const { data: savedData, error: savedError } = await (supabase.from("health_tip_bookmarks" as any) as any)
          .select("tip_id")
          .eq("patient_id", user.id);
        if (savedError) {
          setError(savedError.message);
          setLoading(false);
          return;
        }
        setSavedTipIds(new Set((savedData ?? []).map((bookmark: { tip_id: string }) => bookmark.tip_id)));
      }
      setLoading(false);
    };

    void loadTips();
    return () => { active = false; };
  }, [user]);

  const filteredTips = useMemo(
    () => tips.filter((tip) => {
      const matchesCategory = selectedCategory === "All" || tip.category === selectedCategory;
      const matchesSaved = !showSaved || savedTipIds.has(tip.id);
      return matchesCategory && matchesSaved;
    }),
    [selectedCategory, showSaved, tips, savedTipIds],
  );

  const toggleSaved = async (tipId: string) => {
    if (!user || savingTipId) return;
    setSavingTipId(tipId);
    const isSaved = savedTipIds.has(tipId);
    const result = isSaved
      ? await (supabase.from("health_tip_bookmarks" as any) as any).delete().eq("tip_id", tipId).eq("patient_id", user.id)
      : await (supabase.from("health_tip_bookmarks" as any) as any).insert({ tip_id: tipId, patient_id: user.id });

    if (result.error) {
      setError(result.error.message);
    } else {
      setSavedTipIds((previous) => {
        const next = new Set(previous);
        if (isSaved) next.delete(tipId);
        else next.add(tipId);
        return next;
      });
    }
    setSavingTipId(null);
  };

  return (
    <div className="min-h-screen bg-[#fbfcf8] pb-28 text-[#203f3c]">
      <div className="mx-auto max-w-md px-4 pb-8 pt-5 sm:px-5 sm:pt-7">
        <header className="relative flex items-start justify-center">
          <Link to="/patient-home" aria-label={bn ? "ফিরে যান" : "Go back"} className="absolute left-0 rounded-full p-1.5 text-[#174e49]">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div className="text-center">
            <h1 className="font-serif text-[25px] font-extrabold text-[#174e49]">{bn ? "টিপ দেখুন" : "Explore tips"}</h1>
            <p className="mt-1 text-xs text-[#6d7b78]">{bn ? "ভালো স্বাস্থ্যের জন্য ছোট পদক্ষেপ।" : "Small steps toward better health."}</p>
          </div>
        </header>

        <div className="mt-6 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
          {categories.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setSelectedCategory(value)}
              className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-[10px] font-semibold ${
                selectedCategory === value ? "border-[#168878] bg-[#168878] text-white" : "border-[#e2ebe4] bg-white text-[#647470]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        <section className="mt-7">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-serif text-lg font-extrabold text-[#174e49]">{showSaved ? (bn ? "সংরক্ষিত পোস্ট" : "Saved posts") : (bn ? "সর্বশেষ টিপ" : "Latest tips")}</h2>
            <button
              type="button"
              onClick={() => setShowSaved((visible) => !visible)}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[10px] font-bold ${showSaved ? "bg-[#168878] text-white" : "bg-[#e4f3ef] text-[#237e76]"}`}
            >
              {showSaved ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
              {savedTipIds.size} {bn ? "সংরক্ষিত" : "saved"}
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center rounded-[20px] border border-[#e2ebe4] bg-white py-16">
              <Loader2 className="h-6 w-6 animate-spin text-[#237e76]" />
            </div>
          ) : error ? (
            <div className="rounded-[20px] border border-[#efcaca] bg-[#fff5f5] px-5 py-12 text-center">
              <h3 className="font-serif text-lg font-extrabold text-[#9b5252]">{bn ? "টিপ লোড করা যায়নি" : "Could not load tips"}</h3>
              <p className="mt-2 text-xs text-[#b35f5f]">{error}</p>
            </div>
          ) : filteredTips.length > 0 ? (
            <div className="space-y-3">
              {filteredTips.map((tip) => (
                <TipCard
                  key={tip.id}
                  tip={tip}
                  bn={bn}
                  isSaved={savedTipIds.has(tip.id)}
                  onSave={() => void toggleSaved(tip.id)}
                  onRead={() => setSelectedTip(tip)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-[20px] border border-dashed border-[#cfe3d8] bg-white px-5 py-12 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#e4f3ef] text-[#237e76]">
                <Leaf className="h-8 w-8" />
              </div>
              <h3 className="mt-4 font-serif text-lg font-extrabold text-[#174e49]">
                {tips.length === 0
                  ? (bn ? "এখনও কোনো টিপ নেই" : "No tips yet")
                  : showSaved
                    ? (bn ? "কোনো সংরক্ষিত পোস্ট নেই" : "No saved posts")
                    : (bn ? "এই ক্যাটাগরিতে কোনো টিপ নেই" : "No tips in this category")}
              </h3>
              <p className="mx-auto mt-2 max-w-[250px] text-xs leading-5 text-[#71807d]">
                {tips.length === 0
                  ? (bn ? "আপনার ডাক্তার বা ডায়েটিশিয়ান টিপ শেয়ার করলে এখানে দেখা যাবে।" : "Tips from your doctor or dietitian will appear here when they are shared.")
                  : showSaved
                    ? (bn ? "পোস্টের বুকমার্ক আইকনে চাপ দিয়ে সেভ করুন।" : "Tap the bookmark icon on a post to save it here.")
                    : (bn ? "অন্য একটি ক্যাটাগরি বেছে নিন।" : "Choose another category to see more tips.")}
              </p>
            </div>
          )}
        </section>

        <section className="mt-5 flex items-center gap-3 rounded-[20px] border border-[#dcebe3] bg-[#f0f8f2] p-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e0f2ec] text-[#237e76]">
            <Bookmark className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-[#20534f]">{bn ? "আপনার পছন্দের টিপ সংরক্ষণ করুন" : "Save tips you love"}</h3>
            <p className="mt-1 text-[10px] leading-4 text-[#71807d]">{bn ? "পরে আবার দেখতে টিপ সংরক্ষণ করুন।" : "Bookmark your favorite tips for later."}</p>
          </div>
        </section>

        {selectedTip && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#173f3b]/35 p-3 sm:items-center">
            <div className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-[24px] bg-white p-5 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#237e76]">{selectedTip.category || (bn ? "স্বাস্থ্য" : "Health")}</p>
                  <h2 className="mt-2 font-serif text-2xl font-extrabold leading-tight text-[#174e49]">{selectedTip.title}</h2>
                </div>
                <button type="button" onClick={() => setSelectedTip(null)} aria-label={bn ? "বন্ধ করুন" : "Close"} className="rounded-full bg-[#f0f8f2] p-2 text-[#237e76]">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-4 flex items-center gap-2">
                {selectedTip.author?.profile_photo ? (
                  <img src={selectedTip.author.profile_photo} alt="" className="h-9 w-9 rounded-full object-cover" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e3f2ed] text-sm font-bold text-[#237e76]">
                    {(selectedTip.author?.full_name || "H").charAt(0).toUpperCase()}
                  </div>
                )}
                <p className="text-xs font-bold text-[#20534f]">{selectedTip.author?.full_name || (bn ? "বিশেষজ্ঞ" : "Health professional")}</p>
              </div>
              {selectedTip.image_url && <img src={selectedTip.image_url} alt="" className="mt-4 max-h-60 w-full rounded-2xl object-cover" />}
              <p className="mt-5 whitespace-pre-wrap text-sm leading-7 text-[#536966]">{selectedTip.body}</p>
              <button
                type="button"
                onClick={() => void toggleSaved(selectedTip.id)}
                disabled={savingTipId === selectedTip.id}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl border border-[#cfe3d8] py-3 text-sm font-bold text-[#237e76] disabled:opacity-60"
              >
                {savedTipIds.has(selectedTip.id) ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                {savedTipIds.has(selectedTip.id) ? (bn ? "সংরক্ষিত পোস্ট থেকে সরান" : "Remove from saved posts") : (bn ? "পোস্ট সংরক্ষণ করুন" : "Save post")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AppleIcon = ({ className }: { className?: string }) => <Utensils className={className} />;

export default ExploreTipsPage;